import { Markdown } from '#/components/common/markdown'

import { Accordion } from './accordion'
import s from './faq.module.scss'

const faqs = [
  {
    label: 'Main rules',
    content: `**Who can enter?**

This is an online game jam, so anyone, from anywhere. You can work alone or in teams. There is no limit on the number of people per team and people can be in multiple teams. We suggest a 4-member team with full roles is ideal.
Only one person needs to register and submit the game on behalf of the team.

**What's the theme?**

The theme is announced once the submission phase starts. Your game should follow the theme, and you will be asked how your game fits the theme in submission phase. That being said, you will not be disqualified from the jam if your game doesn't adequately fit the theme.

**When does the jam begin and end?**

Axie Game Jam 2023 begins on Oct 15th at 14:00 GMT+7. The submission ends on Oct 29th at 14:00 GMT+7. You can submit at any time during the submission phase (and we recommend you get your game up early!)`,
  },
  {
    label: 'What you can make',
    content: `
When it comes to what kind of game you can make, we’re ultimately looking for games that are suitable for a general audience and Axie Infinity IP, and that are really easy for people to download, play, and judge. With that in mind...

**What can I make my game in?**

You must produce a digital computer game, that runs on Windows - or web browsers supported by Windows. You can support additional platforms (such as Mac or mobile) if you like, but a Windows / browser build is necessary.

The game file needs to be submitted to Sky Mavis and uploaded to Mavis Hub when you submit.

You can make your game in whatever engine, but we recommend engines such as Unity and Cocos. This will help to integrate with our ecosystem after the jam if you want to develop further.

You can not require the user to download additional software from external sources (so no ROMs for emulators, or games for Roblox. If your game requires additional files (like DLLs) they should be included in the download and not require separate installation).

You can not require the user print things out (so no board or card games).

The game file limit is 400MB. You need to compress your game as a ZIP file.

**Can I make..?**

We try not to put limitations on what people can make, as long as it fits the eligibility discussed above.

Just note that many players will be alone (so they may not be able to adequately judge local multiplayer games) and will only have a few minutes to try your game (so get to the point quickly!)

Your game must NOT contain nudity, or hateful language or visuals. This could see your game being disqualified from being considered as a jam winner. Please consider avoiding extremely strong language and excessive gore, as the jam should be suitable for a general audience. If you’re uncertain, err on the side of being less gratuitous.
`,
  },
  {
    label: 'What you can use',
    content: `
This game jam is about making a game during 2-week period. This is a race to make a game, and everyone starts running when the jam begins! As such, we expect you to make the vast majority of the game during this time.

However, as the jam is primarily about flexing your design chops, we don’t expect you to make everything in the time slot (unless you really want to!) So...

**Can I use pre-existing code?**

Yes! You can use pre-existing code, libraries, and functions. And make use of generic code for things like menus or input.

You can also create things like a blank Unity project, a Github page in advance to save time.

**Can I use pre-existing art and / or audio?**

Yes! You can use whatever art and audio you have the legal right to use. We'd recommend utilizing Axie Generator Tool Kit in your game and some from your own assets - it's up to you.

That means you can use pre-existing sprites, textures, fonts, models, shaders, animations, sound effects, and music - including public domain assets, assets you have purchased, assets you have made yourself, or assets that are (legally-acceptable) derivative works of existing media.

You should credit all external art and audio in your game description.
`,
  },
  {
    label: 'Submitting a game',
    content: `
**How do I submit a game?**

You need to submit your game information and a shared folder of your game package by the submission form. It will be public when submission phase starts.

We will grant you access to our system and upload your game here. From there you can upload the game files, and provide additional details for your game page. You can read more guidelines from our document [here](https://axie-game-jam.gitbook.io/axie-game-jam/about-axie-game-jam/introduction).

**When should I submit the game?**

You can submit the game at any point during the 2-week submission, and we recommend that you get something up nice and early at least 5-6 hours before the submission ends due to high request demands at that time.

Until submissions end, you can change your game informaiton and version as much as you want (such as uploading a new build, or uploading a separate Mac version, or changing the details) and it will automatically change the game that's in the jam. You don't need to un-submit and re-submit."
`,
  },
  {
    label: 'What if I missed the deadline?',
    content: `The submission ends at 14:00 GMT+7. We may add a grace period for late submissions, last minute bug fixes, and Mavis Hub server problems. But after that, submissions will be closed - with no opportunity to upload or change your game.

Once the submission ends, the Axie Game Jam team cannot help you get your game uploaded or changed during this time.

During the voting period, you will not be able to change the game, and must not link to other versions in the description or comments. Whatever's uploaded is what will be played and judged.

If we experience any issues with Mavis Hub's servers due to the load, we may extend the deadline. More details will be on the Axie Game Jam Discord.`,
  },
  {
    label: 'During the voting period',
    content: `**Can I change my game after the jam ends?**

When the jam ends, there's a one more week voting period (details below) where you must not change your game. Uploads to the jam will be rejected, meaning you can no longer provide bug fixes, additional versions, or extra levels.

Also, please do not put links to other builds (such as "Game jam upload has a bug! Please play this one!") in your description or comments. This may see you being disqualified from the jam. Whatever you upload before the jam closes is what people will play and judge.

You can change your game's page during this time though, including the thumbnail, description text, and screenshots. You'll want to do this as fast as possible though, as the majority of the ratings happen at the very start of the rating period.

Once the voting period is over, you're free to update your game however you please, charge for it, release it on other platforms! All the votes after this period will not be counted for the jam.

**Are the games judged?**

Yes! For one week after the voting period ends, people will be able to rate your game against 4 criteria:
- Game Design - How well the game is designed?
- Enjoyment - How enjoyable the game is to play?
- Graphics- How the game looks and sounds?
- Innovation - How interesting, original, creative, and inventive the underlying idea is?

Everyone is eligible to vote games - and will be able to find them on Submissions page of Axie Game Jam 2023 page.

At the end of the voting period, the overall rank for the games will not be immediately shown. Instead, our judges will get to see the games and will play the top voting games in the overall category. We will discuss and then pick the winners.

**How does the ranking algorithm work?**

Mavis Hub takes into account the number of votes a game receives to ensure that things remain fair (for example, a game that receives 5 upvotes/yes ranking will not beat out a game that receives 100 votes with 70 upvotes and 30 downvotes). The exact nature of this algorithm is not made public knowledge.

**Hey, this person is cheating!**

If you suspect foul play or encounter a virus, please use the report button on game details page or send it to our support team on Discord for evaluation`,
  },
  {
    label: 'I need art and audio',
    content: `
Creating your game based on Axie Infinity IP is a required condition of the jam. We provide Axie Generator Tool Kit with all art assets you can use for your game HERE.

Here are some other places where you can find fonts, music, and sound effects for your games. Many of the assets are eligible for use in the jam but check the license before using and provide credit in your game's description!

Fonts
- [Fontsource](https://github.com/fontsource/fontsource)
- [Font Squirrel](https://www.fontsquirrel.com/)
- [Google Fonts](https://fonts.google.com/?preview.text_type=paragraph&stylecount=1)

Audio:
- [Freesound](https://freesound.org/) (SFX)
- [OpenGameArt](https://opengameart.org/) (Music + SFX)
- [Kenney](https://www.kenney.nl/assets?q=audio) (Music + SFX)
- [Soniss](https://sonniss.com/gameaudiogdc) (SFX)
- [Gamesounds](https://gamesounds.xyz/) (SFX)
`,
  },
  {
    label: 'Axie Game Jam Branding Kit',
    content: `Click [here to get logos](https://drive.google.com/drive/folders/1s9ltf5pM8Vv_IHf2w0BBWSSKq3Q3ds_g?usp=sharing), which you may wish to add to your project`,
  },
  {
    label: 'The legal bit',
    content: `- Anything you make during the Axie Game Jam is your property. Sky Mavis claims no ownership of your game.
- Sky Mavis has the right to revoke your game if the game negatively impact to the community and related legal issues using Axie Infinity IP.
- Any game submitted to the Axie Game Jam may show up in Axie Game Jam YouTube video without your express permission.

You can find out more [here](https://hub.skymavis.com/axie-game-jam/terms)`,
  },
]

export const FAQ = () => {
  return (
    <div className={s.container}>
      <h3 className={s.title}>Frequently Asked Questions</h3>
      <div className={s.faq}>
        {faqs.map((item, index) => (
          <Accordion header={item.label} key={index}>
            <Markdown className={s.markdown}>
              {item.content}
            </Markdown>
          </Accordion>
        ))}
      </div>
    </div>
  )
}
